import { Button, Card, Col, Row, Space, Tag } from 'antd';
import { includes } from 'lodash';
import moment from 'moment-timezone';
import React, { useContext, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../AppContext';
import { Import, InfoIcon } from '../../../../../../../assets/svg';
import {
  ADDONS,
  AGENCY_TYPE,
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  DATETIMEWITHBRACKET,
  DATETIMEWITHDIVIDE,
  GA_EVENT,
  GA_LABEL,
  INS_STATUS_CLASSNAME,
  INS_STATUS_KEYS,
  INS_STATUS_LABEL,
} from '../../../../../../../common/constants';
import { Event } from '../../../../../../../common/trackEvents';
import useRouter from '../../../../../../../common/useRouter';
import CanPerform from '../../../../../../../components/CanPerform';
import CommonCard from '../../../../../../../components/CommonCard';
import EllipsisText from '../../../../../../../components/EllipsisText';
import InstructionAnalysisModal from '../InstructionAnalysisModal';
import InstructionRenameModal from './InstructionRenameModal';

const InstructionSummary = ({ instructionData, refetchInstructionData }) => {
  const {
    params: { projectId },
  } = useRouter();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const {
    getToken,
    getCurrentUser,
    getCurrentProjectDetail,
    getCurrentTenant,
    getTenantUser,
  } = useContext(AppContext);
  const tenantId = getCurrentTenant() || getTenantUser()?.tenant?.id;
  const currentTenant = getTenantUser();
  const currentProjectDetails = getCurrentProjectDetail();
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  const currentUser = getCurrentUser();
  const renderDownloadButton = () => {
    const token = getToken();
    const url = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/report/instructions/${instructionData?.id}?access_token=Bearer ${token}&tenantId=${tenantId}`;
    return (
      <Button
        shape="round"
        icon={<Import />}
        className={`report-button b-0 ${
          isDesktopViewport ? '' : 'b-0 width-percent-100 justify-center'
        }`}
        target="_blank"
        href={url}
        disabled={!instructionData}
        onClick={() => {
          Event(GA_EVENT.DOWNLOAD_INSTRUCTION_FINAL_REPORT, {
            label: GA_LABEL.DOWNLOAD_INSTRUCTION_FINAL_REPORT,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            project_id: projectId,
            project_instruction_id: instructionData?.id,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
        }}
      >
        Download Report
      </Button>
    );
  };
  const handleRenameModal = () => {
    setShowRenameModal(true);
    Event(GA_EVENT.VIEW_PROJECT_INSTRUCTION_RENAME_MODAL, {
      label: GA_LABEL.VIEW_PROJECT_INSTRUCTION_RENAME_MODAL,
      // eslint-disable-next-line no-undef
      pathname: window?.location?.href,
      project_id: projectId,
      project_instruction_id: instructionData?.id,
      user_id: currentUser?.id,
      user_name: currentUser?.name,
      tenant_id: currentUser?.tenantUser?.tenant?.id,
      tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
    });
  };

  const handleAnalysisModal = () => {
    setShowAnalysisModal(true);
  };

  const renderAnalysisSection = () => {
    const isQualityCostActive =
      includes(currentTenant?.tenant?.addOn, ADDONS?.QUALITY_COST) &&
      includes(currentProjectDetails?.access, ADDONS?.QUALITY_COST);
    const analysisData = instructionData?.analysis;
    if (analysisData && isQualityCostActive) {
      return (
        <Card className="instruction-analysis-summary mt-20">
          <div className="d-flex align-center justify-between mb-20">
            <h3 className="mb-0">Instruction Analysis</h3>
            {isQualityCostActive &&
              CanPerform({
                action: ALLOWED_ACTION_KEYS.ADD_COQ,
                type: ALLOWED_ACTION_TYPE.BOTH,
              }) && (
                <Button
                  type="primary"
                  shape="round"
                  onClick={handleAnalysisModal}
                  className="update-analysis-button"
                >
                  Update Details
                </Button>
              )}
          </div>
          <Row
            gutter={isDesktopViewport ? [75, 20] : [40, 20]}
            className="fw-medium mb-20"
          >
            <Col span={isDesktopViewport ? 12 : 24}>
              <Space direction="vertical" size={12}>
                <div className="text-secondary">Added By:</div>
                <div>{analysisData?.creator?.name || '-'}</div>
                <div className="text-small">
                  {analysisData?.createdAt &&
                    moment(analysisData?.createdAt)
                      ?.tz(currentProjectDetails?.timeZone)
                      ?.format(DATETIMEWITHDIVIDE)}
                </div>
              </Space>
            </Col>
            <Col span={isDesktopViewport ? 12 : 24}>
              <Space direction="vertical" size={12}>
                <div className="text-secondary">Last Updated By:</div>
                <div>{analysisData?.modifier?.name || '-'}</div>
                <div className="text-small">
                  {analysisData?.updatedAt &&
                    moment(analysisData?.updatedAt)
                      ?.tz(currentProjectDetails?.timeZone)
                      ?.format(DATETIMEWITHDIVIDE)}
                </div>
              </Space>
            </Col>
          </Row>

          <Row
            gutter={isDesktopViewport ? [75, 20] : [40, 20]}
            className="fw-medium"
          >
            <Col span={24}>
              <Space
                direction="vertical"
                size={12}
                className="width-percent-100"
              >
                <div className="text-secondary">Root Cause Analysis:</div>
                <div className="white-space-pre-line">
                  {analysisData?.rootCauseAnalysis ? (
                    <EllipsisText text={analysisData?.rootCauseAnalysis} />
                  ) : (
                    '-'
                  )}
                </div>
              </Space>
            </Col>
            <Col span={24}>
              <Space direction="vertical" size={12}>
                <div className="text-secondary">Correction:</div>
                <div>
                  {analysisData?.correction ? (
                    <EllipsisText text={analysisData?.correction} />
                  ) : (
                    '-'
                  )}
                </div>
              </Space>
            </Col>
            <Col span={24}>
              <Space direction="vertical" size={12}>
                <div className="text-secondary">Corrective Action:</div>
                <div>
                  {analysisData?.correctiveAction ? (
                    <EllipsisText text={analysisData?.correctiveAction} />
                  ) : (
                    '-'
                  )}
                </div>
              </Space>
            </Col>
            <Col span={24}>
              <div className="cost-of-quality-display mt-15">
                <div className="text-secondary mb-10">Cost of Quality:</div>
                <div className="cost-breakdown">
                  {analysisData?.totalCost > 0 ? (
                    <>
                      <div className="cost-total mb-10 fw-semi-bold">
                        Total: {analysisData?.totalCost}/-
                      </div>
                      <div className="cost-components">
                        <Row gutter={[16, 8]}>
                          <Col span={isDesktopViewport ? 6 : 12}>
                            <span className="text-secondary">Labour: </span>
                            <span>
                              {analysisData?.labourCost
                                ? `${analysisData?.labourCost} /-`
                                : '-'}
                            </span>
                          </Col>
                          <Col span={isDesktopViewport ? 6 : 12}>
                            <span className="text-secondary">Materials: </span>
                            <span>
                              {analysisData?.materialCost
                                ? `${analysisData?.materialCost} /-`
                                : '-'}
                            </span>
                          </Col>
                          <Col span={isDesktopViewport ? 6 : 12}>
                            <span className="text-secondary">Machinery: </span>
                            <span>
                              {analysisData?.machineryCost
                                ? `${analysisData?.machineryCost} /-`
                                : '-'}
                            </span>
                          </Col>
                          <Col span={isDesktopViewport ? 6 : 12}>
                            <span className="text-secondary">Others: </span>
                            <span>
                              {analysisData?.otherCost
                                ? `${analysisData?.otherCost} /-`
                                : '-'}
                            </span>
                          </Col>
                        </Row>
                      </div>
                    </>
                  ) : (
                    <div>-</div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      );
    }
    // Render empty state with button to add analysis
    return (
      isQualityCostActive &&
      CanPerform({
        action: ALLOWED_ACTION_KEYS.ADD_COQ,
        type: ALLOWED_ACTION_TYPE.BOTH,
      }) && (
        <CommonCard className="instruction-analysis-empty mt-20">
          <div className="instruction-analysis-empty-content">
            <h4 className="mb-15">Instruction Analysis</h4>
            <p className="text-secondary mb-20">
              Hey! You did not add instruction analysis details yet, please fill
              it by clicking below button
            </p>
            <Button type="primary" shape="round" onClick={handleAnalysisModal}>
              Add Details
            </Button>
          </div>
        </CommonCard>
      )
    );
  };

  return (
    <div className="instruction-tab-details">
      <Card className="instruction-tab-details-summary">
        <div className="d-flex align-center justify-between mb-20">
          <div className="d-flex align-center">
            <h2 className="mb-0">Summary</h2>
            <Tag
              className={`${
                INS_STATUS_CLASSNAME[instructionData?.status]
              } ml-10`}
            >
              {INS_STATUS_LABEL[instructionData?.status]}
            </Tag>
          </div>
          {isDesktopViewport && renderDownloadButton()}
        </div>
        <Row
          gutter={isDesktopViewport ? [75, 40] : [40, 30]}
          className="fw-medium mb-20"
        >
          <Col span={isDesktopViewport ? 6 : 24}>
            <Space direction="vertical" size={12}>
              <Space size={10}>
                <div className="d-flex align-center">
                  <div className="text-secondary mr-5">Name</div>
                  {instructionData?.instructionRenameLogs?.length > 0 && (
                    <InfoIcon className="pointer" onClick={handleRenameModal} />
                  )}
                </div>
              </Space>
              <div>
                <EllipsisText text={instructionData?.name || '-'} />
              </div>
            </Space>
          </Col>
          <Col span={isDesktopViewport ? 6 : 24}>
            <Space direction="vertical" size={12}>
              <div className="text-secondary">Type</div>
              <div>{instructionData?.type || '-'}</div>
            </Space>
          </Col>
          <Col span={isDesktopViewport ? 6 : 24}>
            <Space direction="vertical" size={12}>
              <div className="text-secondary">Team</div>
              {instructionData?.projectAgency?.agency?.type !==
                AGENCY_TYPE.LOCAL && (
                <EllipsisText
                  text={instructionData?.projectAgency?.agency?.name || '-'}
                />
              )}
              {instructionData?.projectAgency?.agency?.type ===
                AGENCY_TYPE.LOCAL && (
                <>
                  <div>Phone Contact</div>
                  {instructionData?.instructionAgencyContacts?.length > 0 && (
                    <div>
                      <EllipsisText
                        text={`${instructionData?.instructionAgencyContacts?.[0]?.user?.name} - ${instructionData?.instructionAgencyContacts?.[0]?.user?.phoneNo}`}
                      />
                    </div>
                  )}
                </>
              )}
            </Space>
          </Col>
          <Col span={isDesktopViewport ? 6 : 24}>
            <Space direction="vertical" size={12}>
              <div className="text-secondary">Due Date</div>
              <div>
                {instructionData?.reminderDate
                  ? moment(instructionData?.reminderDate)
                      ?.tz(currentProjectDetails?.timeZone)
                      ?.format(DATETIMEWITHBRACKET)
                  : '-'}
              </div>
            </Space>
          </Col>
          <Col span={isDesktopViewport ? 6 : 24}>
            <Space direction="vertical" size={12}>
              <div className="text-secondary">Raised By</div>
              <div>{instructionData?.creator?.name || '-'}</div>
              <div>
                {instructionData?.createdAt &&
                  moment(instructionData?.createdAt)
                    ?.tz(currentProjectDetails?.timeZone)
                    ?.format(DATETIMEWITHDIVIDE)}
              </div>
            </Space>
          </Col>
          {instructionData?.status !== INS_STATUS_KEYS.NOTICE && (
            <>
              <Col span={isDesktopViewport ? 6 : 24}>
                <Space direction="vertical" size={12}>
                  <div className="text-secondary">Responded By</div>
                  <div>{instructionData?.responderUser?.name || '-'}</div>
                  <div>
                    {instructionData?.respondedOn &&
                      moment(instructionData?.respondedOn)
                        ?.tz(currentProjectDetails?.timeZone)
                        ?.format(DATETIMEWITHDIVIDE)}
                  </div>
                </Space>
              </Col>
              <Col span={isDesktopViewport ? 6 : 24}>
                <Space direction="vertical" size={12}>
                  <div className="text-secondary">Closed By</div>
                  <div>{instructionData?.closer?.name || '-'}</div>
                  <div>
                    {instructionData?.closedOn &&
                      moment(instructionData?.closedOn)
                        ?.tz(currentProjectDetails?.timeZone)
                        ?.format(DATETIMEWITHDIVIDE)}
                  </div>
                </Space>
              </Col>
              <Col span={isDesktopViewport ? 6 : 24}>
                <Space direction="vertical" size={12}>
                  <div className="text-secondary">Revision</div>
                  <div>
                    {instructionData?.instructionIssueHistories?.[0]
                      ?.revision || '-'}
                  </div>
                </Space>
              </Col>
              <Col span={isDesktopViewport ? 6 : 24}>
                <Space direction="vertical" size={12}>
                  <div className="text-secondary">Location</div>
                  {instructionData?.instructionIssueHistories?.[0]?.latitude &&
                  instructionData?.instructionIssueHistories?.[0]?.longitude ? (
                    <a
                      href={`https://maps.google.com/?q=${instructionData?.instructionIssueHistories?.[0]?.latitude},${instructionData?.instructionIssueHistories?.[0]?.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        Event(GA_EVENT.VIEW_LOCATION, {
                          label: GA_LABEL.VIEW_LOCATION,
                          // eslint-disable-next-line no-undef
                          pathname: window?.location?.href,
                        });
                      }}
                    >
                      View Location
                    </a>
                  ) : (
                    '-'
                  )}
                </Space>
              </Col>
              <Col span={isDesktopViewport ? 6 : 24}>
                <Space direction="vertical" size={12}>
                  <div className="text-secondary">Tags</div>
                  <div>
                    {instructionData?.tags?.length > 0
                      ? instructionData?.tags?.map((tag) => (
                          <div key={tag}>{`#${tag}`}</div>
                        ))
                      : '-'}
                  </div>
                </Space>
              </Col>
            </>
          )}
        </Row>
        {!isDesktopViewport && renderDownloadButton()}
      </Card>

      {instructionData?.status === INS_STATUS_KEYS.CLOSED &&
        renderAnalysisSection()}

      <InstructionRenameModal
        showModal={showRenameModal}
        setShowModal={setShowRenameModal}
        data={instructionData?.instructionRenameLogs}
      />

      {showAnalysisModal && (
        <InstructionAnalysisModal
          showModal={showAnalysisModal}
          setShowModal={setShowAnalysisModal}
          instructionData={instructionData}
          refetchInstructionData={refetchInstructionData}
          isEditMode={!!instructionData?.analysis}
        />
      )}
    </div>
  );
};

export default InstructionSummary;
